// ─────────────────────────────────────────────
//  caseBarStyles — the rules for one case bar on the Timeline chart.
//
//  Split out so `CaseBar.js` stays under the project's 500-line rule. The bar
//  block moved here WHOLESALE from `TimelineChartPage`'s `s` object: it was the
//  largest single run of styling in a 1881-line file for a component that file
//  no longer renders. The comments came with it — every one of them records a
//  measured decision (the hatch alphas, the departure edge, the AA ceiling on
//  the label ink) and re-deriving them would cost the same afternoon twice.
// ─────────────────────────────────────────────
import tokens from "../../theme/tokens";

const { color, font } = tokens;

const S = {
  bar: { position: "absolute", top: 2, bottom: 2, minWidth: 40, border: "1px solid", borderRadius: 4, padding: "0 6px", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden", cursor: "default" },
  barHatch: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "50%",
    zIndex: 0,
    pointerEvents: "none",
    overflow: "hidden",
    display: "flex",
  },
  barHatchFaint: {
    flexShrink: 0,
    paddingLeft: 6,
    fontSize: 11,
    fontWeight: font.weight.bold,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    color: "transparent",
    userSelect: "none",
    borderRight: "1px solid rgba(0,0,0,0.10)",
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(0,0,0,0.055) 0 4px, transparent 4px 10px)",
  },
  barHatchDark: {
    flex: 1,
    minWidth: 0,
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0 5px, transparent 5px 10px)",
    borderRight: "2px solid rgba(0,0,0,0.38)",
  },
  // The CRNA name on a SINGLE-provider bar — and, when the viewer may place
  // people, the box of the button that opens their quick-actions menu.
  //
  // ⚠️ `alignSelf: flex-start` IS A HIT-AREA RULE, NOT AN ALIGNMENT ONE, AND IT
  // IS LOAD-BEARING. `S.bar` is a flex COLUMN, so its cross axis is horizontal
  // and its default `align-items: stretch` had silently blown this item out to
  // the bar's full content width — measured on the deployed board, 2026-08-20:
  // the name trigger came back **186 x 13** on a bar whose text is ~45px wide.
  // Painting is unchanged either way (the text is left-aligned in both), so the
  // stretch was invisible — but hit-testing is not, and the trigger sits at
  // zIndex 3 over the bar's own open-target at zIndex 1. Shrink-to-fit is what
  // keeps "click the NAME → the menu, click the REST of the bar → the case
  // modal" true, and it is what makes it safe to give this box the bar's full
  // HEIGHT (`caseLanes.nameHitPad`) — a full-width AND full-height trigger would
  // have swallowed the open-target whole, and with it the SC 2.5.7 pointer
  // alternative to the drag.
  // ⚠️ COLUMN, SO `flex-start` MEANS LEFT. Move this rule into a flex ROW and
  // the same word means TOP and shrinks the box vertically instead.
  // The trainee riding on the seat holder's bar. Muted and lighter than the
  // name beside it: they are in the room, and they are not the person holding
  // it. Not a colour-only signal — the word "orientee" is right there.
  //
  // ⚠️ IT IS ITS OWN CONTROL NOW, NOT A SUFFIX INSIDE THE SEAT'S BUTTON. The
  // owner reported it on 2026-09-09: *"Both name need to be able to be clicked.
  // Only Michael i can click"* — the chip was a `<span>` INSIDE the seat
  // holder's `<button>`, so every press on the trainee's name opened the SEAT
  // HOLDER's quick-actions menu and the trainee could not be moved at all. A
  // button inside a button is not an option (see `BarName`), so the two names
  // are siblings on a flex ROW and the chip is a `BarName` of its own.
  //
  // Muted and lighter than the name beside it: they are in the room, and they
  // are not the person holding it. Not a colour-only signal — the word
  // "orientee" is right there, and it is the one thing `fitInlineChip` never
  // drops.
  //
  // ⚠️ `flexShrink: 999` IS WHAT KEEPS THE OLD PROTECTION. As a suffix the chip
  // sat at the END of an ellipsised box, so a narrow bar clipped IT and the
  // person's name survived. On a flex row the default is to shrink both in
  // proportion — the name would start losing characters to make room for an
  // annotation. A shrink factor three orders of magnitude larger means the chip
  // gives up every pixel before the name gives up one. `fitInlineChip` normally
  // drops it long before this matters; this is the floor under an estimate.
  barOrienteeChip: {
    fontWeight: font.weight.normal,
    opacity: 0.85,
    fontSize: 11,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 999,
  },
  // The seat holder's name and the trainee's chip, on ONE line.
  //
  // ⚠️ `alignSelf: flex-start` MOVED UP HERE FROM `barLabel`, AND HAD TO. It is
  // a hit-area rule: `S.bar` is a flex COLUMN whose default `align-items:
  // stretch` blows an item out to the bar's full content width, and the name
  // trigger sits at zIndex 3 OVER the bar's open-target. Whatever is the direct
  // child of the column is what has to shrink to fit, and that is now this row.
  //
  // ⚠️ THE ROW ITSELF IS NOT A TARGET. It is `position: static`, so it paints
  // below the absolutely-positioned open-target (zIndex 1) and hit-testing falls
  // through it — clicking the gap between the two names still opens the case
  // modal, which is this chart's SC 2.5.7 alternative to the drag.
  barNameLine: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    alignSelf: "flex-start",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  barLabel: { position: "relative", zIndex: 1, alignSelf: "flex-start", fontSize: 11, fontWeight: font.weight.bold, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  barDepart: { fontWeight: font.weight.regular, opacity: 0.9 },
  // ── TWO PROVIDERS, AS EQUALS ───────────────────────────────────
  //
  // ⚠️ THERE USED TO BE A RANK HERE AND THE OWNER REMOVED IT. The second
  // person was drawn smaller, indented and behind a leading "↳", with
  // "orienting" in the tooltip and the spoken name. His instruction, verbatim:
  // *"i dont care who is orienting who, place the 2 crnas."* So the two bands
  // are the SAME height, the SAME type and the SAME weight. Order is stable
  // (the seat first) because a list needs an order — nothing here may make one
  // band read as under the other.
  //
  // ⚠️ PRESENTATION ONLY. The seat (`ahpId`) and the second-provider fields
  // are still distinct in storage: a write has to know which slot it is
  // updating, and the seat still holds the room for `isSoloCase`. What was
  // rejected is the DISPLAY of the relationship, not the data model.
  //
  // A band is absolutely positioned across the bar's padding box so its hatch
  // child can be a percentage of the BAR (`barMetrics` geometry), while its
  // text keeps the bar's own 6px inset. `overflow: hidden` because a name wider
  // than the bar must clip at the band, not spill onto the next one.
  providerBand: {
    position: "absolute",
    left: 0,
    right: 0,
    padding: "0 6px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
  },
  // One provider's name inside their band. Identical for both of them.
  //
  // ⚠️ THE LINE HEIGHT IS SET BY THE CALLER to the band's height in px, which
  // is what centres a single line inside a 24px band WITHOUT making this a flex
  // container — `text-overflow: ellipsis` only works on a block container, and
  // a name too long for its bar has to end in an ellipsis rather than a hard
  // cut. See `domain/caseBarLabel` for why the name is the last thing to go.
  // ⚠️ SHRINK-TO-FIT, NEVER `flex: 1`. Stretching this to the band's width
  // would make the whole line a quick-actions trigger, and clicking anywhere on
  // a person's row would stop opening the case modal. The name is the control;
  // the rest of the band is the bar. (`min-width: auto` on a flex item with
  // `overflow: hidden` already resolves to 0, so the ellipsis still works
  // without an explicit `minWidth: 0` overriding the trigger's 24px floor.)
  providerLine: {
    position: "relative",
    zIndex: 1,
    fontSize: 11,
    fontWeight: font.weight.bold,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // The surgeon · supervising-MD line, in a paired bar.
  //
  // ⚠️ IT COMES BACK, ALWAYS. The first two-provider attempt gave its row to
  // the second CRNA and the owner caught it immediately: *"inside is missing
  // the supervisor and the surgeon."* A fact displaced off a bar must never be
  // a fact dropped from the board.
  //
  // ⚠️ `display: flex` IS LOAD-BEARING, AND THIS IS THE BUG IT FIXES.
  // Measured in Chrome on the built app, 2026-08-20: as a plain block this band
  // was 12px tall with a scrollHeight of 20, and the surgeon line inside it sat
  // at y=6 with a height of 13 — so 7px of a 13px line was cut off by the
  // `overflow: hidden` below. The owner saw it as the third line of a paired bar
  // sliced through the middle and spilling toward the next room.
  //
  // The mechanism: this div sets no font. It therefore inherits `16px /
  // normal` from `body`, and an INLINE child sits on a line box whose height is
  // driven by that 16px STRUT — not by the child's own `line-height: 12px`. The
  // strut pushes the shared baseline ~14.5px down, so a 10px name starts below
  // the halfway mark of a 12px box and its lower half lands outside the clip.
  // `display: flex` blockifies the child, which is how the two provider bands
  // above have always escaped this (they were flex from the first commit); the
  // surgeon line was the one band that was not, which is exactly why it was the
  // one band that clipped.
  //
  // ⚠️ SETTING A HEIGHT ON A BAND IS NOT ENOUGH TO MAKE ITS TEXT FIT. Any band
  // added here must either be a flex container or carry its own font-size AND
  // line-height. jsdom performs no layout, so no render test can see this —
  // `caseBarStyles.test.js` asserts the rule instead ("a band must not
  // leave its text on an inherited strut").
  subBand: { position: "absolute", left: 0, right: 0, padding: "0 6px", overflow: "hidden", display: "flex", alignItems: "center" },
  // Per-provider shift hatching. Same vocabulary as `barHatch` — same diagonals,
  // same faint/dark split, same departure rule at the right edge — but filling
  // ONE band instead of the top half of the bar, so each provider's leaving
  // time is drawn on their own line. Reusing the language is the point: the
  // board already reads diagonals as "this person has gone".
  bandHatch: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: "none",
    overflow: "hidden",
    display: "flex",
  },
  barOnCall: { borderStyle: "dashed", borderWidth: 2 },
  // ── THE CLICK TARGET THAT OPENS THE CASE ───────────────────────────────────
  //
  // Transparent and absolute so it covers the whole bar WITHOUT containing the
  // name, which is the sibling control above it (zIndex 3). The bar itself is
  // no longer the button — see the structure note at the top of `CaseBarShell`.
  openTarget: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background: "transparent",
    border: 0,
    padding: 0,
    cursor: "pointer",
  },
  // ── THE NAME, AS A CONTROL ─────────────────────────────────────────────────
  //
  // Above the open-target (zIndex 3 vs 1) so a click on the name opens the
  // quick-actions menu and a click anywhere else opens the case modal. Every
  // button default is reset, because the visible result must be identical to
  // the span it replaces — 17 of 19 rooms are looking at this line all day and
  // a 1px shift in the CRNA name reads as the board having changed.
  //
  // ⚠️ ZERO PADDING AND ZERO MARGIN HERE, BUT NOT ON THE ELEMENT — AND THAT
  // CHANGED FOR A MEASURED REASON. This rule used to argue that padding the
  // trigger to a 24px-tall target would "either shift every CRNA name on the
  // board or overflow a 26px content box", and spend the vertical axis on the
  // wall display's row height instead. The first half was right; the conclusion
  // was not. Measured in Chrome on the deployed board, 2026-08-20, the trigger
  // came back **186 x 13** — half of WCAG 2.2 SC 2.5.8's 24px floor on a control
  // the AIC presses all day.
  //
  // The resolution is `caseLanes.nameHitPad`: padding that grows the box plus an
  // equal NEGATIVE margin that returns it to the layout, so the box reaches the
  // bar's full inner height and the flex column above still measures this item
  // at one 12.65px line. Nothing moves, by construction. It is spread in by the
  // CALLER (`CaseBar`'s single-provider branch), because only the caller knows
  // whether the surgeon line is drawn under this name and how tall the row is;
  // the two paired bands need none of it — they already hand the button a 24px
  // `line-height` and measure 24.
  //
  // ⚠️ THE ZEROS BELOW STILL HAVE TO GO FIRST. They are shorthands, and React
  // writes inline styles in key order, so `padding: 0` set before the caller's
  // `paddingTop` is harmless while the reverse would silently erase the target.
  // `minWidth: 24` keeps the horizontal axis legal for a one-character name.
  nameTrigger: {
    position: "relative",
    zIndex: 3,
    background: "transparent",
    border: 0,
    borderRadius: 3,
    margin: 0,
    padding: 0,
    minWidth: 24,
    maxWidth: "100%",
    textAlign: "left",
    // ⚠️ A `<button>` DOES NOT INHERIT TYPE. The UA stylesheet hands it
    // `400 13.333px Arial`, which on the second line (10px) would print the
    // name a third larger than the seat's and blow the lane's 13px content box.
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    color: "inherit",
    cursor: "pointer",
  },
  // The procedure · surgeon · ⚕ MD line. Decoration: `aria-hidden`, never a
  // control, and the one thing on a single-provider bar that is allowed to sit
  // in the name trigger's new full-height box.
  //
  // ⚠️ `pointerEvents: none` REPAIRS A DEAD STRIP, IT DOES NOT CREATE ONE. This
  // span is `position: relative; zIndex: 1` — the SAME layer as the bar's
  // open-target — and it comes LATER in the DOM, so it painted over it and ate
  // every click on the bottom half of a single-provider bar. Nothing happened:
  // the span has no handler and the bar container has none either, so the case
  // modal was simply unreachable there. Making the span transparent to the
  // pointer hands those pixels back to whatever is underneath — the name trigger
  // for its own width, the open-target for the rest.
  barSub: { position: "relative", zIndex: 1, pointerEvents: "none", fontSize: 10, opacity: 0.85, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // Visually hidden, screen-reader visible — the standard clip. This carries
  // the bar's FULL sentence (both people, both departures, surgeon + MD) while
  // the visible spans carry only what the width allowed. `position: absolute`
  // so it costs the flex column no height in a 26px box.
  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 },
  // ── THE DRAG EDGE ──────────────────────────────────────────────────────────
  //
  // ⚠️ THE VISIBLE INDICATOR IS GONE, THE FUNCTION IS NOT. There used to be a
  // 2px vertical grip rule painted at each edge (a `linear-gradient` in the
  // bar's own ink at 0.55 opacity). The product owner asked for it removed
  // (2026-08-20): on a 19-room board it drew 38 vertical lines that read as
  // structure — tick marks, room boundaries, case splits — none of which they
  // were, and the staff who use this board already know the bars drag.
  //
  // What is deliberately KEPT, and must stay kept: the full 24px hit zone
  // (SC 2.5.8), `touchAction: none` so the browser cannot steal the gesture,
  // `setPointerCapture`, the keyboard sliders, the focus ring below, and the
  // `col-resize` cursor — the affordance survives as a HOVER state, which is
  // discoverable to a newcomer without painting anything at rest.
  handle: {
    position: "absolute",
    top: 0,
    bottom: 0,
    zIndex: 2,
    cursor: "col-resize",
    touchAction: "none", // the browser must not steal the gesture for a scroll
  },
  // ⚠️ INSET (`outlineOffset: -3`). `s.bar` clips its children, so a ring drawn
  // outside the handle is simply not painted — the one state that must be
  // visible would be the one that is not. With the resting ink gone this ring
  // is now the ONLY thing that ever draws a handle, which makes it load-bearing
  // rather than decorative.
  handleFocus: { outline: "3px solid currentColor", outlineOffset: -3 },
  tip: {
    position: "absolute",
    top: -20,
    // Above the now-line, which is zIndex 6.
    zIndex: 7,
    pointerEvents: "none",
    background: color.text.primary,
    color: "#fff",
    fontSize: 10,
    fontWeight: font.weight.bold,
    padding: "1px 5px",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
};

export default S;
